import React from 'react';
import * as yup from "yup";
import {useFormik} from "formik";

import {useMainLayout} from "../../../layouts/Main";
import {useApartmentRatesQuery, useUpdateRateMutation} from "../query";
import {PeopleTag} from "../RatePlanner/plannerTag";
import RatePlansToolbar from "./RatePlansToolbar";

import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import CardContent from "@mui/material/CardContent";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import GroupIcon from '@mui/icons-material/Group';
import TextField from "@mui/material/TextField";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import PetsIcon from "@mui/icons-material/Pets";
import InputAdornment from "@mui/material/InputAdornment";
import Box from "@mui/material/Box";
import LoadingButton from "@mui/lab/LoadingButton";
import PriceCentField from "../../../components/PriceCentField";
import Switch from "@mui/material/Switch/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import {useRates} from "../RateContext";


const RatePlans = () => {
    const {accUnitSelector} = useRates();
    const {selectedAccommodationUnit} = accUnitSelector;

    const {isMobile} = useMainLayout();
    const ratesQuery = useApartmentRatesQuery(selectedAccommodationUnit?.id);

    const baseRate = ratesQuery.data?.find(rate => rate.isBase === true);

    // Note we set the RateCard key so the component re-render and the formik values are reset
    // when the rate (or the apartment) changes.
    return <Container disableGutters={isMobile}
                      maxWidth="md">
        <Stack spacing={4} mb={4} pt={3}>
            <RatePlansToolbar />
            {
                selectedAccommodationUnit && baseRate && <RateCard key={baseRate.id}
                                                                   rate={baseRate}
                                                                   apartmentId={selectedAccommodationUnit.id}/>
            }
        </Stack>
    </Container>
}


const formatNumber = (n) => {
    // given a number n or a string representing a number return a string with the number formatted with the + or - sign
    return Number(n) > 0 ? `+${Number(n)}` : n;
}

const RateCard = ({rate, apartmentId}) => {
    const updateRate = useUpdateRateMutation({apartmentId,rateId: rate.id});

    const positiveIntegerSchema = yup.number()
        .positive("Deve essere un numero positivo")
        .integer("Deve essere un numero intero")
        .required("Campo obbligatorio")
        .typeError("Deve essere un numero");

    const nonNegativeIntegerSchema = yup.number()
        .integer("Deve essere un numero intero")
        .min(0, "Deve essere maggiore o uguale a 0")
        .required("Campo obbligatorio")
        .typeError("Deve essere un numero");

    const percentageSchema = yup.number()
        .integer("Deve essere un numero intero")
        .min(-100, `Deve essere comprese tra -100 e 100`)
        .max(500, `Deve essere comprese tra -100 e 500`)
        .required("Campo obbligatorio")
        .typeError("Deve essere un numero");

    const formik = useFormik({
        initialValues: {
            name: rate.name,
            basePeople: rate.basePeople,
            minPeople: rate.minPeople,
            maxPeople: rate.maxPeople,
            priceVariations: rate.priceVariations.map(v => ({
                people: v.people,
                percentage: v.percentage
            })).sort((v, v1) => v1.people - v.people),
            petsAllowed: rate.petsAllowed,
            petsFeeCent: rate.petsFeeCent
        },
        validationSchema: yup.object().shape({
            basePeople: positiveIntegerSchema
                .min(yup.ref("minPeople"), "Deve essere maggiore o uguale all'occupazione minima")
                .max(yup.ref("maxPeople"), "Deve essere minore o uguale all'occupazione massima"),
            minPeople: positiveIntegerSchema,
            maxPeople: positiveIntegerSchema
                .min(yup.ref("minPeople"), "Deve essere maggiore o uguale all'occupazione minima")
            ,
            priceVariations: yup.array().of(yup.object().shape({
                people: positiveIntegerSchema,
                percentage: percentageSchema, // TODO when people > basePeople the percentage must be positive otherwise negative
            })),
            petsAllowed: yup.boolean().required("Campo obbligatorio"),
            petsFeeCent: nonNegativeIntegerSchema
        }),
        onSubmit: async (values, submitProps) => {
            // changed attributes
            // const changes = Object.keys(values).reduce((acc, key) => {
            //     if(key === "priceVariations") {
            //         const changedPriceVariations = values.priceVariations.filter((variation, index) => {
            //             return Number(variation.percentage) !== rate.priceVariations[index].percentage;
            //         }).map(variation => {
            //             return {
            //                 id: variation.id,
            //                 percentage: Number(variation.percentage)
            //             }
            //         })
            //         if(changedPriceVariations.length > 0) {
            //             acc[key] = changedPriceVariations;
            //         }
            //     }else{
            //         if (rate[key] !== Number(values[key])) {
            //             acc[key] = Number(values[key]);
            //         }
            //     }
            //     return acc;
            // }, {});

            await updateRate.mutateAsync(values);

            // reset the form once the data is saved
            submitProps.resetForm({ values });
        }
    });

    const handleChangeMaxPeople = (event) => {
        const maxPeople = Number(event.target.value);
        if(maxPeople > 0){
            const minPeople = Math.min(maxPeople, formik.values.minPeople);
            const basePeople = Math.min(maxPeople, formik.values.basePeople);
            const priceVariations = computePriceVariations(minPeople, maxPeople, basePeople);
            formik.setValues(prev => ({...prev, maxPeople, minPeople, basePeople, priceVariations}));
        }
        else{
            // set exactly the value the user entered (eventually an empty space)
            formik.setFieldValue("maxPeople", event.target.value);
        }
    }

    const handleChangeMinPeople = (event) => {
        const minPeople = Number(event.target.value);
        if(minPeople > 0){
            const basePeople = Math.max(minPeople, formik.values.basePeople);
            const maxPeople = Math.max(minPeople, formik.values.maxPeople);
            const priceVariations = computePriceVariations(minPeople, maxPeople, basePeople);
            formik.setValues(prev => ({...prev, minPeople, maxPeople, basePeople, priceVariations}));
        }
        else{
            // set exactly the value the user entered (eventually an empty space)
            formik.setFieldValue("minPeople", event.target.value);
        }
    }

    const handleChangeBasePeople = (event) => {
        const basePeople = Number(event.target.value);
        if(basePeople > 0){
            const minPeople = Math.min(basePeople, formik.values.minPeople);
            const maxPeople = Math.max(basePeople, formik.values.maxPeople);
            const priceVariations = computePriceVariations(minPeople, maxPeople, basePeople);
            formik.setValues(prev => ({...prev, basePeople, minPeople, maxPeople, priceVariations}));
        }else{
            // set exactly the value the user entered (eventually an empty space)
            formik.setFieldValue("basePeople", event.target.value);
        }
    }

    const computePriceVariations = (minPeople, maxPeople, basePeople) => {
        if(!maxPeople > 0 &&  !minPeople > 0 && !basePeople > 0) {
            return formik.values.priceVariations;
        }

        const variations = Array.from(
            {length: maxPeople - minPeople + 1},
            (_, i) =>  maxPeople - i)

        const unitVariation = Math.floor(100 / maxPeople * 2 / 3);

        return variations.filter(people => people !== basePeople).map(people => ({
                people,
                percentage: unitVariation * (people - basePeople)
            }
        ))
    }

    const showSave = formik.dirty

    return <Card>
        <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Tariffa Base</Typography>
                <LoadingButton variant="contained"
                               startIcon={<SaveIcon/>}
                               disabled={!showSave}
                               size="small"
                               onClick={formik.handleSubmit}
                               loading={updateRate.isLoading}>
                    Salva
                </LoadingButton>
            </Stack>
            <Stack spacing={3} mt={4}>
                <Typography textAlign="justify">
                    Imposta la capacità base, l'occupazione minima e massima per l'apppartamento. La capacità base
                    è il numero di persone che la struttura prevede di ospitare. La capacità massima corrisponde
                    alla capacità base più eventuali posti letti extra.
                </Typography>
                <RateField formik={formik}
                           name={"basePeople"}
                           label={"Capacità base"}
                           icon={<GroupIcon/>}
                           onChange={handleChangeBasePeople}
                           />
                <RateField formik={formik}
                           name={"maxPeople"}
                           label={"Occupazione massima"}
                           icon={<PersonAddIcon/>}
                           onChange={handleChangeMaxPeople}/>
                <RateField formik={formik}
                           name={"minPeople"}
                           label={"Occupazione minima"}
                           icon={<PersonRemoveIcon/>}
                           onChange={handleChangeMinPeople}/>
            </Stack>
            <Stack spacing={3} mt={4}>
                <Typography textAlign="justify">
                    Imposta la variazione di prezzo per ogni persona in più o in meno rispetto alla capacità
                    base. Dovrai riconfigurare i prezzi per applicare le modifiche.
                </Typography>
                {
                    formik.values.priceVariations.map((variation, index) => (
                        <RateField key={variation.people}
                                   formik={formik}
                                   name={`priceVariations[${index}].percentage`}
                                   value={formatNumber(formik.values.priceVariations[index].percentage)}
                                   label={`Variazione % ${variation.people} persone`}
                                   icon={<PeopleTag peopleNumber={variation.people} variant="contained"/>}
                                   error={formik.touched.priceVariations?.[index]?.percentage && Boolean(formik.errors.priceVariations?.[index]?.percentage)}
                                   helperText={formik.touched.priceVariations?.[index]?.percentage && formik.errors.priceVariations?.[index]?.percentage}/>
                    ))
                }
            </Stack>
            <Stack spacing={3} mt={4}>
                <Typography textAlign="justify">
                    Configura se accettare gli animali domestici e imposta un supplemento giornaliero per ogni animali
                    domestico. Il supplemento totale sarà calcolato in base al numero di animali domestici e alla lunghezza
                    del soggiorno e sarà aggiunto al prezzo totale.
                </Typography>
                <StackWithIcon icon={<PetsIcon/>}>
                    <FormGroup>
                        <FormControlLabel control={<Switch checked={formik.values.petsAllowed}
                                                       onChange={formik.handleChange}
                                                       name="petsAllowed"/>}
                                                       label={`Animali domestici ${!!formik.values.petsAllowed ? '' : 'NON'} accettati, clicca per cambiare`}/>
                    </FormGroup>
                </StackWithIcon>
                <StackWithIcon icon={
                    <Stack direction="row" justifyContent="center" alignItems="center">
                        € / <PetsIcon fontSize="small"/>
                    </Stack>
                }>
                    <PriceCentField name="petsFeeCent"
                                    label="Supplemento giornaliero per ogni animale domestico"
                                    fullWidth
                                    disabled={!formik.values.petsAllowed}
                                    onChange={(priceCent) => formik.setFieldValue("petsFeeCent", priceCent)}
                                    value={formik.values.petsFeeCent}
                                    error={formik.touched.petsFeeCent&& Boolean(formik.errors.petsFeeCent)}
                                    helperText={formik.touched.petsFeeCent && formik.errors.petsFeeCent}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                                        endAdornment: <InputAdornment position="start"><EditIcon/></InputAdornment>
                                    }}/>
                </StackWithIcon>
            </Stack>
        </CardContent>
    </Card>
}

const RateField = ({formik, label, name, icon, value=null, onChange, errors, helperText, ...textFieldProps}) => {
    return <StackWithIcon icon={icon}>
        <TextField name={name}
                   label={label}
                   value={value === null ? formik.values[name] : value}
                   onChange={onChange || formik.handleChange}
                   error={errors || (formik.touched[name] && Boolean(formik.errors[name]))}
                   helperText={helperText || (formik.touched[name] && formik.errors[name])}
                   fullWidth
                   InputProps={{
                       endAdornment: (
                           <InputAdornment position="start">
                               <EditIcon/>
                           </InputAdornment>
                       ),
                   }}
                   variant="standard"
                   {...textFieldProps}/>
    </StackWithIcon>
}

const StackWithIcon = ({icon, children}) => {
    return <Stack direction="row" spacing={3} alignItems="center">
        {icon && <Box sx={{color: 'rgba(0, 0, 0, 0.54)', minWidth: '56px', textAlign: "center"}}>{icon}</Box>}
        {children}
    </Stack>
}

export default RatePlans;